/**
 * usePortfolioLevelData Hook
 * Unified React Query hook for portfolio-level data.
 * Replaces the manual PortfolioAggregator + useEffect pattern
 * in PortfolioDashboardContainer.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portfolioApiService } from '../services/api/portfolioApiService';
import type { ClientDashboardData, GlobalDashboardData } from '../services/portfolio-refactor/aggregation/PortfolioAggregator';

interface PortfolioLevelParams {
  level: 'my-book' | 'client' | 'portfolio';
  clientId?: string;
  portfolioId?: string;
  period?: string;
  benchmark?: string;
}

interface UsePortfolioLevelDataReturn {
  clientData: ClientDashboardData | null;
  globalData: GlobalDashboardData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

const PERIOD_MAP: Record<string, string> = {
  '1d': '7d',
  '1w': '7d',
  '1m': '30d',
  '3m': '90d',
  '6m': '90d',
  'ytd': 'ytd',
  '1y': '1y',
  'all': 'all',
};

function mapPeriod(period: string): string {
  return PERIOD_MAP[period] || period;
}

export function usePortfolioLevelData(params: PortfolioLevelParams): UsePortfolioLevelDataReturn {
  const queryClient = useQueryClient();
  const { level, clientId, portfolioId, period = '30d', benchmark } = params;
  const backendPeriod = mapPeriod(period);

  // Client/portfolio-level data query
  const clientQuery = useQuery({
    queryKey: ['portfolio-dashboard', level, clientId, portfolioId, backendPeriod, benchmark],
    queryFn: async (): Promise<ClientDashboardData> => {
      const effectiveClientId = clientId || '';
      const effectivePortfolioId = level === 'portfolio' ? portfolioId : undefined;

      const [
        overview,
        positionsResponse,
        allocationResponse,
        cashResponse,
        performanceResponse,
        riskResponse,
        transactionsResponse,
        attributionResponse,
        rollingReturns3mResponse,
        rollingReturns12mResponse,
        correlationResponse,
        varDistributionResponse,
      ] = await Promise.all([
        portfolioApiService.getPortfolioOverview(effectiveClientId, effectivePortfolioId),
        portfolioApiService.getPositions(effectiveClientId, effectivePortfolioId),
        portfolioApiService.getAllocation(effectiveClientId, effectivePortfolioId),
        portfolioApiService.getCashPositions(effectiveClientId, effectivePortfolioId),
        portfolioApiService.getPerformance(effectiveClientId, backendPeriod as '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all', effectivePortfolioId, benchmark),
        portfolioApiService.getRiskMetrics(effectiveClientId, effectivePortfolioId),
        portfolioApiService.getTransactions(effectiveClientId),
        portfolioApiService.getPerformanceAttribution(effectiveClientId).catch(() => null),
        portfolioApiService.getRollingReturns(effectiveClientId, '3m').catch(() => null),
        portfolioApiService.getRollingReturns(effectiveClientId, '12m').catch(() => null),
        portfolioApiService.getCorrelationMatrix(effectiveClientId).catch(() => null),
        portfolioApiService.getVarDistribution(effectiveClientId).catch(() => null),
      ]);

      const perfAny = performanceResponse as any;

      return {
        overview,
        positions: positionsResponse.positions,
        allocation: allocationResponse.allocation,
        cash: cashResponse.cash_positions,
        totalCashCHF: cashResponse.total_cash_chf,
        performance: performanceResponse.metrics,
        performanceHistory: performanceResponse.history,
        performanceAttribution: attributionResponse?.attribution ?? null,
        rollingReturns: rollingReturns3mResponse?.rolling_returns || [],
        rollingReturns12m: rollingReturns12mResponse?.rolling_returns || [],
        topContributors: perfAny?.top_contributors || [],
        bottomContributors: perfAny?.bottom_contributors || [],
        risk: riskResponse.risk_metrics,
        stressTests: riskResponse.stress_tests,
        correlationMatrix: correlationResponse?.correlation_matrix ?? null,
        varDistribution: varDistributionResponse?.var_distribution || [],
        transactions: transactionsResponse.transactions || [],
      };
    },
    enabled: level !== 'my-book' && !!clientId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Global (my-book) data query
  const globalQuery = useQuery({
    queryKey: ['portfolio-dashboard-global'],
    queryFn: async (): Promise<GlobalDashboardData> => {
      const result = await portfolioApiService.getGlobalDashboardData();
      return {
        portfolios: result.portfolios,
        summary: result.summary,
      };
    },
    enabled: level === 'my-book',
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const refetch = () => {
    if (level === 'my-book') {
      queryClient.invalidateQueries({ queryKey: ['portfolio-dashboard-global'] });
    } else {
      queryClient.invalidateQueries({
        queryKey: ['portfolio-dashboard', level, clientId, portfolioId, backendPeriod, benchmark],
      });
    }
  };

  if (level === 'my-book') {
    return {
      clientData: null,
      globalData: globalQuery.data ?? null,
      loading: globalQuery.isLoading,
      error: globalQuery.error as Error | null,
      refetch,
    };
  }

  return {
    clientData: clientQuery.data ?? null,
    globalData: null,
    loading: clientQuery.isLoading,
    error: clientQuery.error as Error | null,
    refetch,
  };
}
